import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDevicesThunk,
  toggleDeviceThunk,
  exportDevicesThunk,
  updateDevicePrefsThunk,
} from "../../../store/thunks/deviceThunks";
import {
  setDeviceFilters,
  clearDeviceError,
} from "../../../store/slices/deviceSlice";
import DeviceCard from "../components/DeviceCard";
import DeviceFilterBar from "../components/DeviceFilterBar";
import DevicePrefsModal from "../components/DevicePrefsModal";
import Button from "../../../components/common/Button";
import EmptyState from "../../../components/common/EmptyState";
import { notifySuccess, notifyError } from "../../../utils/notification";
import { DeviceMobileIcon, DownloadIcon } from "@heroicons/react/outline";

const DeviceRegistry = () => {
  const dispatch = useDispatch();
  const { rows, loading, error, filters, pagination } = useSelector(
    (state) => state.notificationDevices
  );

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [isPrefsModalOpen, setIsPrefsModalOpen] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    // Initial load
    dispatch(
      fetchDevicesThunk({
        filters,
        reset: true,
      })
    );
  }, [dispatch, filters]);

  useEffect(() => {
    if (error) {
      notifyError(error);
      dispatch(clearDeviceError());
    }
  }, [error, dispatch]);

  const handleLoadMore = async () => {
    if (!pagination.hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      await dispatch(
        fetchDevicesThunk({
          filters,
          cursor: pagination.nextCursor,
          reset: false,
        })
      ).unwrap();
    } catch (error) {
      notifyError("Failed to load more devices");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleToggleDevice = async (deviceId, isActive) => {
    try {
      await dispatch(toggleDeviceThunk({ deviceId, isActive })).unwrap();
      notifySuccess(`Device ${isActive ? "activated" : "deactivated"}`);
    } catch (error) {
      notifyError(error);
    }
  };

  const handleEditPrefs = (device) => {
    setSelectedDevice(device);
    setIsPrefsModalOpen(true);
  };

  const handleSavePrefs = async (deviceId, updates) => {
    try {
      await dispatch(
        updateDevicePrefsThunk({
          deviceId,
          prefs: updates.prefs,
          quietHours: updates.quietHours,
        })
      ).unwrap();
      notifySuccess("Preferences updated successfully");
      setIsPrefsModalOpen(false);
    } catch (error) {
      notifyError("Failed to update preferences");
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await dispatch(exportDevicesThunk(filters)).unwrap();
      notifySuccess("Devices exported successfully");
    } catch (error) {
      notifyError("Failed to export devices");
    } finally {
      setIsExporting(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    dispatch(setDeviceFilters(newFilters));
  };

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100
      ) {
        handleLoadMore();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [pagination.hasMore, isLoadingMore, filters]);

  return (
    <div className="container mx-auto p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Device Registry
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage registered devices and push tokens
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleExport}
          loading={isExporting}
          className="flex items-center gap-2"
        >
          <DownloadIcon className="w-4 h-4" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <DeviceFilterBar filters={filters} onFilterChange={handleFilterChange} />

      {/* Devices Grid */}
      {loading && rows.length === 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="h-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"
            />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={DeviceMobileIcon}
          title="No devices found"
          description="No devices match your current filters"
        />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {rows.map((device) => (
              <DeviceCard
                key={device._id}
                device={device}
                onToggle={handleToggleDevice}
                onEditPrefs={handleEditPrefs}
              />
            ))}
          </div>

          {/* Load More Indicator */}
          {isLoadingMore && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}

          {!pagination.hasMore && rows.length > 0 && (
            <p className="text-center text-gray-500 py-4">
              No more devices to load
            </p>
          )}
        </>
      )}

      {/* Device Preferences Modal */}
      <DevicePrefsModal
        isOpen={isPrefsModalOpen}
        onClose={() => setIsPrefsModalOpen(false)}
        device={selectedDevice}
        onSave={handleSavePrefs}
      />
    </div>
  );
};

export default DeviceRegistry;
